import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp
} from "firebase/firestore";
import { db, auth } from "../Firebase";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from "firebase/storage";
import { toast } from "react-toastify";
import "../styles/EditCourse.css";

/* ================= HELPERS ================= */
const cleanArray = arr =>
  arr.filter(item =>
    Object.values(item).every(v => String(v).trim())
  );

const EditCourse = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const storage = getStorage();

  /* ================= STATES ================= */
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");

  const [price, setPrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");

  const [isFree, setIsFree] = useState(false);
  const [freePreview, setFreePreview] = useState(false);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState("");

  const [sections, setSections] = useState([{ title: "", description: "" }]);
  const [faqs, setFaqs] = useState([{ q: "", a: "" }]);

  const [hasStudents, setHasStudents] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ================= FETCH COURSE ================= */
  useEffect(() => {
    const fetchCourse = async () => {
      const snap = await getDoc(doc(db, "courses", id));

      if (!snap.exists()) {
        toast.error("Course not found");
        navigate("/teacherCourse");
        return;
      }

      const data = snap.data();

      setTitle(data.title || "");
      setDescription(data.description || "");
      setCategory(data.category || "");

      setPrice(data.price ?? "");
      setDiscountPercent(data.discountPercent ?? "");
      setIsFree(Boolean(data.isFree));
      setFreePreview(Boolean(data.freePreviewEnabled));

      setThumbnailUrl(data.thumbnail || "");

      setSections(data.sections?.length ? data.sections : [{ title: "", description: "" }]);
      setFaqs(data.faqs?.length ? data.faqs : [{ q: "", a: "" }]);
    };

    const checkStudents = async () => {
      const q = query(
        collection(db, "enrollments"),
        where("courseId", "==", id)
      );
      const snap = await getDocs(q);
      if (!snap.empty) setHasStudents(true);
    };

    fetchCourse();
    checkStudents();
  }, [id, navigate]);

  /* ================= SECTION HANDLERS ================= */
  const handleSectionChange = (index, field, value) => {
    const updated = [...sections];
    updated[index][field] = value;
    setSections(updated);
  };

  const addSection = () =>
    setSections([...sections, { title: "", description: "" }]);

  const removeSection = index =>
    setSections(sections.filter((_, i) => i !== index));

  /* ================= FAQ HANDLERS ================= */
  const handleFaqChange = (index, field, value) => {
    const updated = [...faqs];
    updated[index][field] = value;
    setFaqs(updated);
  };

  const addFaq = () => setFaqs([...faqs, { q: "", a: "" }]);

  const removeFaq = index =>
    setFaqs(faqs.filter((_, i) => i !== index));

  /* ================= UPDATE ================= */
  const handleUpdate = async () => {
    if (!title || !description || !category) {
      toast.warning("Fill required fields");
      return;
    }

    try {
      setLoading(true);

      const basePrice = isFree ? 0 : Number(price || 0);
      const discount = isFree ? 0 : Number(discountPercent || 0);

      const discountedPrice =
        !isFree && discount
          ? Math.round(basePrice - (basePrice * discount) / 100)
          : basePrice;

      /* ===== THUMBNAIL ===== */
      let finalThumbnail = thumbnailUrl;

      if (thumbnailFile && !hasStudents) {
        const thumbRef = ref(storage, `course-thumbnails/${id}`);
        await uploadBytes(thumbRef, thumbnailFile);
        finalThumbnail = await getDownloadURL(thumbRef);
      }

      const updateData = {
        price: basePrice,
        discountPercent: discount,
        discountedPrice,
        isFree,
        freePreviewEnabled: freePreview,
        sections: cleanArray(sections),
        faqs: cleanArray(faqs),
        updatedAt: serverTimestamp()
      };

      if (!hasStudents) {
        updateData.title = title;
        updateData.description = description;
        updateData.category = category;
        updateData.thumbnail = finalThumbnail;
      }

      await updateDoc(doc(db, "courses", id), updateData);

      await addDoc(collection(db, "course_audit_logs"), {
        courseId: id,
        editedBy: auth.currentUser.uid,
        editedAt: serverTimestamp(),
        changes: updateData
      });

      toast.success("Course updated!");
      navigate("/teacherCourse");

    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="edit-course-page">
      <div className="edit-course-card">
        <h1>Edit Course</h1>

        {hasStudents && (
          <div className="warning-box">
            Students enrolled. Core details locked.
          </div>
        )}

        <div className="form-group">
          <label>Title</label>
          <input
            disabled={hasStudents}
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            disabled={hasStudents}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Category</label>
          <select
            disabled={hasStudents}
            value={category}
            onChange={e => setCategory(e.target.value)}
          >
            <option value="">Select category</option>
            <option value="Programming">Programming</option>
            <option value="Design">Design</option>
            <option value="Business">Business</option>
            <option value="Marketing">Marketing</option>
          </select>
        </div>

        <div className="form-group">
          <label>Price</label>
          <input
            type="number"
            disabled={isFree}
            value={price}
            onChange={e => setPrice(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Discount (%)</label>
          <input
            type="number"
            disabled={isFree}
            value={discountPercent}
            onChange={e => setDiscountPercent(e.target.value)}
          />
        </div>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={isFree}
            onChange={e => {
              setIsFree(e.target.checked);
              if (e.target.checked) setDiscountPercent(0);
            }}
          />
          Free Course
        </label>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={freePreview}
            onChange={e => setFreePreview(e.target.checked)}
          />
          First video free preview
        </label>

        {!hasStudents && (
          <div className="form-group">
            <label>Thumbnail</label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setThumbnailFile(e.target.files[0])}
            />
            {thumbnailUrl && (
              <img
                src={thumbnailUrl}
                alt="thumbnail"
                className="thumb-preview"
              />
            )}
          </div>
        )}

        {/* SECTIONS */}
        <div className="sections-box">
          <h3>Sections</h3>

          {sections.map((sec, i) => (
            <div key={i} className="section-card">
              <input
                value={sec.title}
                onChange={e =>
                  handleSectionChange(i, "title", e.target.value)
                }
                placeholder="Section title"
              />

              <textarea
                value={sec.description}
                onChange={e =>
                  handleSectionChange(i, "description", e.target.value)
                }
                placeholder="Section description"
              />

              {sections.length > 1 && (
                <button
                  className="remove-btn"
                  onClick={() => removeSection(i)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}

          <button className="add-btn" onClick={addSection}>
            + Add Section
          </button>
        </div>

        {/* FAQ */}
        <div className="sections-box">
          <h3>FAQs</h3>

          {faqs.map((faq, i) => (
            <div key={i} className="section-card">
              <input
                value={faq.q}
                onChange={e =>
                  handleFaqChange(i, "q", e.target.value)
                }
                placeholder="Question"
              />

              <textarea
                value={faq.a}
                onChange={e =>
                  handleFaqChange(i, "a", e.target.value)
                }
                placeholder="Answer"
              />

              {faqs.length > 1 && (
                <button
                  className="remove-btn"
                  onClick={() => removeFaq(i)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}

          <button className="add-btn" onClick={addFaq}>
            + Add FAQ
          </button>
        </div>

        <div className="action-buttons">
          <button className="cancel-btn" onClick={() => navigate(-1)}>
            Cancel
          </button>

          <button
            className="submit-btn"
            onClick={handleUpdate}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Course"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCourse;
